import React, { useCallback, useEffect, useState } from 'react';
import '../../css/mainOutlet.css';
import { Header, HeaderProfile, Confirmation, Button } from '@core/uilibrary';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../../../footer/ts/sidebar';
import SclLogo from '../../../../../asset-controller-mod/asset-serv/images/SclLogo.png';
import { BackAuthIcon, LogOutIcon, SettingIcon } from '../../../../../asset-controller-mod/asset-serv/icons/iconExporter';
import NotificationButton from '../../../../../component-controller-mod/component-serv/right-sidebar-fs-cp/rs-notification-op/notification-button/ts/notificationButton';
import NotificationSidebar from '../../../../../component-controller-mod/component-serv/right-sidebar-fs-cp/rs-notification-op/notification-sidebar/ts/notificationSidebar';
import {
  NotificationSidebarProvider,
  useNotificationSidebar,
} from '../../../../../context-controller-mod/context-serv/global-provider/right-sidebar-context/ts/notificationSidebarContext';
import Cookies from 'js-cookie';
import { API_ENDPOINTS } from '../../../../../context-controller-mod/context-serv/global-provider/api-shell/api-provider';
import { useGlobalPopupProvider } from '../../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider';
import apiPostAuth from '../../../../../api-controller-mod/api-serv/post-api/postApiAuth';
import { useToast } from '../../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider';
import { useCookies } from '../../../../../context-controller-mod/context-serv/global-provider/cookies-context/cookiesContext';
import NavigationGuard from '../../../../../route-controller-mod/route-serv/route-controller/routeGuard';
import { groupPermissionAccessCMSNotification, groupPermissionAccessCMSSetting } from '../../../../../context-controller-mod/context-serv/permission-provider/permissionExporter';
import { subscribeNotificationSSE, ApiNotificationItem } from '../../../../../component-controller-mod/component-serv/right-sidebar-fs-cp/rs-notification-op/notification-sidebar/ts/notificationData.type';

const AUTHENTICATION_URL = process.env.REACT_APP_URL_AUTHENTICATION;
const AUTH_URL_LOGIN = `${AUTHENTICATION_URL}/login`;
const BASE_URL = `${process.env.REACT_APP_API_URL_AUTH}`;

const MainOutletInner: React.FC = () => {
  const username = Cookies.get('username');
  const email = Cookies.get('email');
  const profile = Cookies.get('profile');

  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const { openConfirm, closeConfirm } = useGlobalPopupProvider();
  const { logoutController } = useCookies();
  const navigate = useNavigate();
  const location = useLocation();
  const { toggle, close, isOpen } = useNotificationSidebar();
  const { showToast } = useToast();

  const isSettingRoute = location.pathname.startsWith('/setting');
  const [loading, setLoading] = useState(true); //NOSONAR

  const canAccessNotification = groupPermissionAccessCMSNotification.view();
  const canAccessSetting = groupPermissionAccessCMSSetting.view();
  const [unreadCount, setUnreadCount] = useState(0);
  const [realtimeNotifItem, setRealtimeNotifItem] = useState<ApiNotificationItem | null>(null);
  const [sidebarKey, setSidebarKey] = useState(0);

  // ── Back to authentication ─────────────────────────────────────────────────
  const backToAuthentication = () => {
    openConfirm(
      <Confirmation
        visible
        title="Back to authentication?"
        description="You sure Back to authentication?"
        status="warning"
        icon={<LogOutIcon />}
        onClose={closeConfirm}
        buttons={
          <>
            <Button variant="ghost" size="md" value="Cancel" type="button" onClick={closeConfirm} />
            <Button variant="primary" size="md" value="Confirm" type="button"
              onClick={() => { globalThis.window.location.href = AUTH_URL_LOGIN; closeConfirm(); }}
            />
          </>
        }
      />
    );
  };

  // ── Log out ────────────────────────────────────────────────────────────────
  const logOut = () => {
    openConfirm(
      <Confirmation
        visible
        title="Log out?"
        description="Your current stage will be log out all application!"
        status="error"
        icon={<LogOutIcon />}
        onClose={closeConfirm}
        buttons={
          <>
            <Button variant="ghost" size="md" value="Cancel" type="button" onClick={closeConfirm} />
            <Button variant="primary" size="md" value="Confirm" type="button"
              onClick={() => { logOutConfirm(); closeConfirm(); }}
            />
          </>
        }
      />
    );
  };

  const logOutConfirm = useCallback(async () => {
    try {
      const response = await apiPostAuth(API_ENDPOINTS.LOGOUT);
      if (response.status === 200) {
        logoutController();
        globalThis.window.location.href = AUTH_URL_LOGIN;
      }
    } catch (err) { //NOSONAR
      showToast({
        status: 'error',
        title: 'Logout failed!',
        description: 'Cannot Logout please contact to admin or try again!',
      });

    } finally {
      setLoading(false);
    }
  }, []);

  const profileMenuItems = isSettingRoute
    ? [
      { key: 'logout', label: 'Back to Authentication', icon: <BackAuthIcon />, onClick: backToAuthentication },
      { key: 'logout1', label: 'Logout', icon: <LogOutIcon />, onClick: logOut },
    ]
    : [
      ...(canAccessSetting
        ? [{
          key: 'setting',
          label: 'Setting',
          icon: <SettingIcon />,
          onClick: () => navigate('/setting'),
        }]
        : []),
      { key: 'logout', label: 'Back to Authentication', icon: <BackAuthIcon />, onClick: backToAuthentication },
      { key: 'logout1', label: 'Logout', icon: <LogOutIcon />, onClick: logOut },
    ];

  // ── See more handler ───────────────────────────────────────────────────────
  const handleSeeMore = () => {
    close();
    navigate('/notification');
  };

  // ── Toggle notification sidebar ────────────────────────────────────────────
  const handleNotifClick = () => {
    setUnreadCount(0);
    setSidebarKey(prev => prev + 1);
    toggle(
      <NotificationSidebar
        key={sidebarKey}
        visible={true}
        onSeeMore={handleSeeMore}
        realtimeItem={realtimeNotifItem}
      />,
    );
  };

  useEffect(() => {
    if (!profile) return;

    setAvatarUrl(`${BASE_URL}/api/v1/user/vdi/${profile}`);
  }, [profile]);

  // ── SSE subscription ────────────────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = subscribeNotificationSSE((item: ApiNotificationItem) => {
      setRealtimeNotifItem(item);

      if ((item.readStatus ?? '').toUpperCase() === 'UNREAD') {
        setUnreadCount((prev) => prev + 1);

        setTimeout(() => {
          showToast({
            status: 'info',
            title: typeof item.message === 'string' ? item.message : (item.message?.module ?? 'New Notification'),
            description: typeof item.message === 'string' ? '' : (item.message?.message ?? 'You have a new notification.'),
          });
        }, 5000);
      }
    });
    return unsubscribe;
  }, []);
  return (
    <div className='scl--main-layout-container'>

      <NavigationGuard />

      {/* ── Fixed Header ── */}
      <div className='scl--app-fixed-header'>
        <Header
          logoAlt='CMS LOGO'
          name='ALETHEIA'
          logoSrc={SclLogo}
          onBrandClick={() => navigate('/overview')}
          sticky={false}
          rightContent={
            <div className='scl--main-outlet-header-right'>

              {/* ── Notification bell ── */}
              <div className="scl--main-outlet-header-right-notificationBtn">
                {canAccessNotification &&
                  <NotificationButton
                    count={unreadCount}
                    active={isOpen}
                    onClick={handleNotifClick}
                  />
                }

              </div>

              <HeaderProfile
                name={username || 'User'}
                email={email || ''}
                menuItems={profileMenuItems}
                avatarUrl={avatarUrl}
              />

            </div>
          }
        />
      </div>

      {/* ── One Sidebar, two configs ── */}
      <div className='scl--sidebar-fixed-container'>
        <Sidebar />
      </div>

      {/* ── Main Content ── */}
      <main className='scl--outlet-main-container'>
        <div className='scl--main-content-mid-style-container'>
          <Outlet />
        </div>
      </main>

    </div>
  );
};

// ── Exported component — wraps inner with NotificationSidebarProvider ─────────
const MainOutlet: React.FC = () => (
  <NotificationSidebarProvider>
    <MainOutletInner />
  </NotificationSidebarProvider>
);

export default MainOutlet;